import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  EnvelopeIcon, 
  LockClosedIcon, 
  FireIcon,
  ArrowPathIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline'
import { FcGoogle } from 'react-icons/fc'
import toast from 'react-hot-toast'
import logo from '../assets/logo.png'

const Login = () => {
  const { t } = useTranslation()
  const { login, forgotPassword, googleSignIn } = useAuth()
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const hasAttemptedAutoLogin = useRef(false)

  const pageClass = darkMode
    ? 'min-h-screen bg-sport-darker'
    : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-cyan-50'
  const mutedTextClass = darkMode ? 'text-sport-silver/60' : 'text-gray-600'
  const labelTextClass = darkMode ? 'text-sport-silver' : 'text-gray-700'
  const iconTextClass = darkMode ? 'text-sport-silver/50' : 'text-gray-400'
  const headingTextClass = darkMode ? 'text-sport-light' : 'text-gray-900'
  const successIconWrapClass = darkMode
    ? 'w-16 h-16 bg-green-500/25 ring-1 ring-green-400/30 rounded-full flex items-center justify-center mx-auto'
    : 'w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto'
  const brandWordmarkClass = "font-['Orbitron'] font-black italic uppercase leading-none text-white drop-shadow-[0_2px_0_rgba(11,18,32,0.9)]"
  const brandWordmarkStyle = {
    WebkitTextStroke: '1px rgba(13, 27, 55, 0.9)',
    textShadow: '0 2px 0 rgba(13,27,55,0.95), 0 0 16px rgba(161, 232, 255, 0.25)'
  }

  useEffect(() => {
    if (hasAttemptedAutoLogin.current) {
      return
    }

    const handoffPrefix = 'GIOCHIAMO_AUTH_HANDOFF:'
    let shouldAutoLogin = false
    let incomingEmail = ''
    let incomingPassword = ''

    if (typeof window.name === 'string' && window.name.startsWith(handoffPrefix)) {
      try {
        const handoffRaw = window.name.slice(handoffPrefix.length)
        const handoff = JSON.parse(handoffRaw)
        const isFresh = typeof handoff.createdAt === 'number' && Date.now() - handoff.createdAt < 2 * 60 * 1000

        if (handoff.autologin && isFresh) {
          shouldAutoLogin = true
          incomingEmail = handoff.email || ''
          incomingPassword = handoff.password || ''
        }
      } catch (error) {
        console.error('Invalid auth handoff payload:', error)
      } finally {
        window.name = ''
      }
    }

    if (!shouldAutoLogin || !incomingEmail || !incomingPassword) {
      return
    }

    hasAttemptedAutoLogin.current = true

    setEmail(incomingEmail)
    setPassword(incomingPassword)

    window.history.replaceState({}, document.title, location.pathname)

    const performAutoLogin = async () => {
      setLoading(true)
      const result = await login(incomingEmail, incomingPassword)
      setLoading(false)

      if (result.success) {
        navigate('/', { replace: true })
      }
    }

    performAutoLogin()
  }, [location.pathname, location.search, navigate, login])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const result = await login(email, password)
    if (result.success) {
      navigate('/')
    }
    setLoading(false)
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    if (!resetEmail) {
      toast.error(t('auth.enterEmailAddress'))
      return
    }
    
    setResetLoading(true)
    const result = await forgotPassword(resetEmail)
    setResetLoading(false)
    
    if (result.success) {
      setResetSent(true)
    }
  }

  const closeResetConfirmation = () => {
    setResetSent(false)
    setShowForgotPassword(false)
    setResetEmail('')
  }

  const handleGoogleSignIn = async () => {
    // Check if popup is likely to be blocked
    const isPopupBlocked = () => {
      // Test if popup blocker might be active
      const testWindow = window.open('about:blank', '_blank', 'width=1,height=1')
      if (testWindow) {
        testWindow.close()
        return false
      }
      return true
    }

    // Show warning if popup blocker is detected
    if (isPopupBlocked()) {
      toast.error(
        t('auth.popupBlocked'), 
        { 
          duration: 5000,
          icon: '🔔'
        }
      )
      // Provide instructions
      toast((t) => (
        <div>
          <p className="font-semibold mb-1">Popup Blocked!</p>
          <p className="text-sm mb-2">Please allow popups for this site and try again.</p>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-sport-primary text-white rounded-lg text-sm"
          >
            Got it
          </button>
        </div>
      ), { duration: 7000 })
    }

    setLoading(true)
    const result = await googleSignIn()
    setLoading(false)
    
    if (result.success) {
      navigate('/')
    } else if (result.error === 'auth/popup-blocked') {
      // Additional guidance for popup blocked
      toast.error('Please allow popups for this website to sign in with Google', {
        duration: 6000,
      })
    }
  }

  return (
    <div className={`${pageClass} flex items-center justify-center p-4 relative overflow-hidden`}>
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sport-primary/10 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-sport-secondary/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
      </div>

      <AnimatePresence mode="wait">
        {!showForgotPassword ? (
          <motion.div
            key="login"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.3 }}
            className="card-sport max-w-md w-full p-8 relative z-10"
          >
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-block relative"
              >
                <img
                  src={logo}
                  alt={t('app.brand')}
                  className="h-20 w-20 mx-auto object-contain drop-shadow-2xl"
                />
                <div className="absolute inset-0 bg-sport-primary/30 rounded-full blur-2xl -z-10 animate-pulse-glow"></div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex items-center justify-center gap-2 mt-4"
              >
                <FireIcon className="h-5 w-5 text-sport-secondary" />
                <span className="text-sport-secondary font-semibold tracking-wider uppercase text-sm">{t('login.welcomeBack')}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={`${brandWordmarkClass} -skew-x-6 mt-2 text-3xl tracking-[0.04em]`}
                style={brandWordmarkStyle}
              >
                {t('app.brand').toUpperCase()}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className={`mt-2 ${mutedTextClass}`}
              >
                {t('login.signInToContinue')}
              </motion.p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${labelTextClass}`}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <EnvelopeIcon className={`absolute left-4 top-3.5 h-5 w-5 ${iconTextClass}`} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-modern pl-12"
                    placeholder="email@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${labelTextClass}`}>
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <LockClosedIcon className={`absolute left-4 top-3.5 h-5 w-5 ${iconTextClass}`} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-modern pl-12"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-sport-primary hover:text-sport-secondary transition-colors"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-gradient btn-glow w-full"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-sport-dark border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  t('auth.login')
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${darkMode ? 'border-sport-silver/20' : 'border-gray-300'}`}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${darkMode ? 'bg-sport-darker text-sport-silver/60' : 'bg-white text-gray-500'}`}>
                  {t('auth.orContinueWith')}
                </span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                darkMode
                  ? 'bg-sport-gray/20 hover:bg-sport-gray/30 text-sport-silver border border-sport-silver/20'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm'
              }`}
            >
              <FcGoogle className="w-5 h-5" />
              <span>{t('auth.signInWithGoogle')}</span>
            </button>

            <div className="mt-6 text-center">
              <p className={mutedTextClass}>
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-sport-primary hover:text-sport-secondary font-semibold transition-colors">
                  {t('auth.register')}
                </Link>
              </p>
            </div>

            <p className={`mt-4 text-center text-xs ${mutedTextClass}`}>
              {t('login.privacyCookiePolicyRead')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="forgot-password"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="card-sport max-w-md w-full p-8 relative z-10"
          >
            <button
              onClick={() => {
                setShowForgotPassword(false)
                setResetSent(false)
                setResetEmail('')
              }}
              className="flex items-center gap-2 text-sport-primary hover:text-sport-secondary transition-colors mb-6"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              <span>{t('common.back')}</span>
            </button>

            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold mb-2 ${headingTextClass}`}>{t('auth.resetPassword')}</h2>
              <p className={`text-sm ${mutedTextClass}`}>
                {t('auth.resetPasswordInstructions')}
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${labelTextClass}`}>
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <EnvelopeIcon className={`absolute left-4 top-3.5 h-5 w-5 ${iconTextClass}`} />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="input-modern pl-12"
                    placeholder="email@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="btn-gradient btn-glow w-full"
              >
                {resetLoading ? (
                  <div className="flex items-center justify-center">
                    <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                    {t('common.sending')}
                  </div>
                ) : (
                  t('auth.sendResetLink')
                )}
              </button>
            </form>

            <AnimatePresence>
              {resetSent && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-sport-dark/70 px-4 backdrop-blur-sm"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className={`w-full max-w-sm rounded-2xl border p-6 shadow-2xl ${darkMode ? 'border-sport-silver/15 bg-sport-darker' : 'border-gray-200 bg-white'}`}
                  >
                    <div className="text-center space-y-4">
                      <div className={successIconWrapClass}>
                        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className={`text-xl font-semibold ${headingTextClass}`}>{t('auth.checkYourEmail')}</h3>
                        <p className={`mt-2 text-sm ${mutedTextClass}`}>
                          {t('auth.passwordResetLinkSent', { email: resetEmail })}
                        </p>
                        <p className={`mt-2 text-sm ${mutedTextClass}`}>
                          {t('auth.checkSpamFolder')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={closeResetConfirmation}
                        className="btn-gradient btn-glow w-full"
                      >
                        {t('auth.ok')}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Login